'use client';

import React, { useEffect, useLayoutEffect } from 'react';
import { usePathname } from 'next/navigation';
import { createPortal } from 'react-dom';
import { AppThemeProvider } from '../components/providers/mui-theme-provider';
import { QueryProvider } from '../components/providers/query-provider';
import NextAuthClientProvider from '../components/NextAuthClientProvider';
import dynamic from 'next/dynamic';
import ClientHeader from './ClientHeader';
import ClientFooter from './ClientFooter';
const ThemeToggle = dynamic(() => import('@letsgo/theme-toggle').then(m => m.ThemeToggle || m), { ssr: false, loading: () => null });

/** Routes that use the authenticated app shell (dashboard only). Do not show public header/footer on these. Auth pages (sign-in, sign-up) get the public header/footer. */
function isAuthenticatedRoute(pathname: string | null) {
  if (!pathname) return false;
  return pathname.startsWith('/dashboard');
}

interface ClientProvidersProps {
  children: React.ReactNode;
  session?: any;
  isAuthenticated?: boolean;
}

/**
 * Dev/QA mock session (local only):
 * - Set localStorage dev_signin=1 to enable. Optionally set dev_session to a JSON session object.
 * - Only active when NODE_ENV is 'development' (gated). Do not use in production.
 * - Purpose: QA and local dev without real auth (client components hydrate as authenticated).
 */
const DEV_MOCK_SESSION_ENABLED =
  typeof process !== 'undefined' && process.env.NODE_ENV === 'development';

export default function ClientProviders({ children, session, isAuthenticated }: ClientProvidersProps) {
  const pathname = usePathname();
  const [localSession, setLocalSession] = React.useState<any | null>(null);
  // Show public header/footer on any route that is not the dashboard (so logged-in users on landing can navigate to Dashboard).
  const showPublicShell = !isAuthenticatedRoute(pathname);

  React.useEffect(() => {
    try {
      if (typeof window === 'undefined' || !DEV_MOCK_SESSION_ENABLED) return;
      const devFlag = window.localStorage.getItem('dev_signin');
      if (devFlag === '1') {
        // eslint-disable-next-line no-console -- intentional dev-only signal
        console.info(
          '[FloAI dev] Mock session is ON (localStorage dev_signin=1). Header will show Dashboard/Profile as if signed in. Remove dev_signin (Application → Local Storage) to see the real signed-out state.'
        );
        const devSessionJson = window.localStorage.getItem('dev_session');
        if (devSessionJson) {
          try {
            setLocalSession(JSON.parse(devSessionJson));
            return;
          } catch { /* fallthrough to create default */ }
        }
        setLocalSession({
          user: { id: 'dev-user', email: 'dev@local', name: 'Dev Tester', image: null, role: 'admin' },
          expires: new Date(Date.now() + 1000 * 60 * 60).toISOString(),
        });
      }
    } catch (e) {
      // ignore
    }
  }, []);
  useEffect(() => {
    try {
      // (previous DOM-cleanup removed) Keep only accessibility wiring and non-invasive client-side helpers.

      // Auto-label IconButtons that lack aria-label using heuristics
      const iconButtons = Array.from(document.querySelectorAll('button.MuiIconButton-root')) as HTMLButtonElement[];
      iconButtons.forEach((btn, idx) => {
        if (btn.getAttribute('aria-label')) return; // already labeled
        // Try to infer from child image alt
        const img = btn.querySelector('img[alt]') as HTMLImageElement | null;
        if (img && img.alt) {
          btn.setAttribute('aria-label', img.alt);
          return;
        }
        // Try to infer from child svg title or aria-label
        const svg = btn.querySelector('svg');
        if (svg) {
          const svgLabel = svg.getAttribute('aria-label') || svg.querySelector('title')?.textContent;
          if (svgLabel) {
            btn.setAttribute('aria-label', svgLabel);
            return;
          }
        }
        // Fallback: use nearby text (previous sibling) if exists
        const prevText = (btn.previousElementSibling && btn.previousElementSibling.textContent?.trim()) || '';
        if (prevText) {
          btn.setAttribute('aria-label', prevText);
          return;
        }
        // Final fallback: generic label with index to reduce Axe hits
        btn.setAttribute('aria-label', `button-${idx+1}`);
      });

      // Ensure Quill picker labels and options have accessible names
      try {
        document.querySelectorAll('.ql-picker-label').forEach((el) => {
          if (el instanceof HTMLElement && !el.getAttribute('aria-label')) {
            el.setAttribute('aria-label', 'Header level');
          }
        });
        document.querySelectorAll('.ql-picker-item').forEach((it, idx) => {
          if (it instanceof HTMLElement && !it.getAttribute('aria-label')) {
            const val = it.getAttribute('data-value') || '';
            const label = val === '1' ? 'Heading 1' : val === '2' ? 'Heading 2' : val === '3' ? 'Heading 3' : (val === '' ? 'Normal' : val);
            it.setAttribute('aria-label', label);
          }
        });
      } catch (e) {
        // ignore
      }

    // Ensure Quill toolbar buttons have accessible names (covers dynamically rendered Quill controls)
    try {
      const quillMappings: [string, string][] = [
        ['.ql-bold', 'Bold'],
        ['.ql-italic', 'Italic'],
        ['.ql-underline', 'Underline'],
        ['.ql-strike', 'Strikethrough'],
        ['.ql-blockquote', 'Block quote'],
        ['select.ql-header', 'Header level'],
        ['.ql-list[value=\"ordered\"]', 'Ordered list'],
        ['.ql-list[value=\"bullet\"]', 'Bulleted list'],
        ['.ql-link', 'Insert link'],
        ['.ql-image', 'Insert image'],
        ['.ql-video', 'Insert video'],
        ['.ql-clean', 'Clear formatting'],
      ];
      const toolbar = document.querySelector('.ql-toolbar');
      if (toolbar) {
        quillMappings.forEach(([sel, label]) => {
          const el = toolbar.querySelector(sel) as HTMLElement | HTMLSelectElement | null;
          if (el) {
            if (!el.getAttribute('aria-label')) el.setAttribute('aria-label', label);
            // If this is a select (header picker), ensure it has a label and is focusable
            if (el instanceof HTMLSelectElement) {
              if (!el.getAttribute('aria-label')) el.setAttribute('aria-label', label);
            }
            // If the element has no textual content (icon-only), add a visually-hidden span to provide accessible name
            if (!el.textContent || el.textContent.trim() === '') {
              const existing = el.querySelector('.sr-only');
              if (!existing) {
                const span = document.createElement('span');
                span.className = 'sr-only';
                span.textContent = label;
                // Apply visually-hidden styles inline to avoid relying on external CSS
                span.setAttribute('style', 'position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0;');
                el.appendChild(span);
              }
            }
          }
        });
      }
    } catch (e) {
      // ignore non-critical quill labeling errors
    }
    // Also use a MutationObserver to react to toolbar DOM changes (Quill may re-render buttons)
    try {
      const observeToolbar = (tb: Element) => {
        try {
          const observer = new MutationObserver((mutations) => {
            mutations.forEach(() => {
              // Re-apply labels for known mappings when mutations occur
              const quillMappingsInner: [string, string][] = [
                ['.ql-bold', 'Bold'],
                ['.ql-italic', 'Italic'],
                ['.ql-underline', 'Underline'],
                ['.ql-strike', 'Strikethrough'],
                ['.ql-blockquote', 'Block quote'],
                ['.ql-list[value=\"ordered\"]', 'Ordered list'],
                ['.ql-list[value=\"bullet\"]', 'Bulleted list'],
                ['.ql-link', 'Insert link'],
                ['.ql-image', 'Insert image'],
                ['.ql-video', 'Insert video'],
                ['.ql-clean', 'Clear formatting'],
              ];
              quillMappingsInner.forEach(([sel, label]) => {
                const el = (tb as HTMLElement).querySelector(sel) as HTMLElement | HTMLSelectElement | null;
                if (el) {
                  if (!el.getAttribute('aria-label')) el.setAttribute('aria-label', label);
                  if (el instanceof HTMLSelectElement && !el.getAttribute('aria-label')) el.setAttribute('aria-label', label);
                  if (!el.textContent || el.textContent.trim() === '') {
                    const existing = el.querySelector('.sr-only');
                    if (!existing) {
                      const span = document.createElement('span');
                      span.className = 'sr-only';
                      span.textContent = label;
                      span.setAttribute('style', 'position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0;');
                      el.appendChild(span);
                    }
                  }
                }
              });
              // Ensure header picker options/text are present and labeled
              try {
                const headerSelect = (tb as HTMLElement).querySelector('select.ql-header') as HTMLSelectElement | null;
                if (headerSelect) {
                  const headerLabels = ['Heading 1', 'Heading 2', 'Heading 3', 'Normal'];
                  Array.from(headerSelect.options).forEach((opt, idx) => {
                    try {
                      if (!opt.text || opt.text.trim() === '') {
                        opt.text = headerLabels[idx] || opt.value || 'Header option';
                      }
                    } catch (e) {
                      // ignore
                    }
                  });
                  if (!headerSelect.getAttribute('aria-label')) headerSelect.setAttribute('aria-label', 'Header level');
                }
                (tb as HTMLElement).querySelectorAll('.ql-picker-label').forEach((pl) => {
                  if (pl instanceof HTMLElement && !pl.getAttribute('aria-label')) pl.setAttribute('aria-label', 'Header level');
                });
                (tb as HTMLElement).querySelectorAll('.ql-picker-item').forEach((it, idx) => {
                  if (it instanceof HTMLElement && !it.getAttribute('aria-label')) {
                    const val = it.getAttribute('data-value') || '';
                    const label = val === '1' ? 'Heading 1' : val === '2' ? 'Heading 2' : val === '3' ? 'Heading 3' : (val === '' ? 'Normal' : val);
                    it.setAttribute('aria-label', label);
                  }
                });
              } catch (e) {
                // ignore
              }
            });
          });
          observer.observe(tb, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'aria-label', 'title'] });
        } catch (e) {
          // ignore observer errors
        }
      };
      const tb = document.querySelector('.ql-toolbar');
      if (tb) observeToolbar(tb);
      else {
        // Wait for toolbar to appear, then attach observer
        const wait = setInterval(() => {
          const tb2 = document.querySelector('.ql-toolbar');
          if (tb2) {
            clearInterval(wait);
            observeToolbar(tb2);
          }
        }, 200);
        setTimeout(() => clearInterval(wait), 5000);
      }
    } catch (e) {
      // ignore
    }
    // Retry a few times to catch cases where Quill initializes slightly later (e.g., headless scans)
    try {
      let attempts = 0;
      const retry = setInterval(() => {
        attempts += 1;
        const toolbar = document.querySelector('.ql-toolbar');
        if (toolbar) {
          // run the same mapping logic quickly
          const quillMappings: [string, string][] = [
            ['.ql-bold', 'Bold'],
            ['.ql-italic', 'Italic'],
            ['.ql-underline', 'Underline'],
            ['.ql-strike', 'Strikethrough'],
            ['.ql-blockquote', 'Block quote'],
              ['select.ql-header', 'Header level'],
            ['.ql-list[value=\"ordered\"]', 'Ordered list'],
            ['.ql-list[value=\"bullet\"]', 'Bulleted list'],
            ['.ql-link', 'Insert link'],
            ['.ql-image', 'Insert image'],
            ['.ql-video', 'Insert video'],
            ['.ql-clean', 'Clear formatting'],
          ];
          quillMappings.forEach(([sel, label]) => {
            const el = toolbar.querySelector(sel) as HTMLElement | null;
            if (el && !el.getAttribute('aria-label')) {
              el.setAttribute('aria-label', label);
              if (!el.textContent || el.textContent.trim() === '') {
                const existing = el.querySelector('.sr-only');
                if (!existing) {
                  const span = document.createElement('span');
                  span.className = 'sr-only';
                  span.textContent = label;
                  span.setAttribute('style', 'position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0;');
                  el.appendChild(span);
                }
              }
            }
          });
          clearInterval(retry);
        }
        if (attempts > 15) clearInterval(retry);
      }, 200);
    } catch (e) {
      // ignore
    }
      // Additional hygiene: make hidden Quill picker option lists unfocusable and ensure spans with aria-label have role=button
      try {
        document.querySelectorAll('.ql-picker-options[aria-hidden=\"true\"]').forEach((el) => {
          if (el instanceof HTMLElement) el.setAttribute('tabindex', '-1');
        });
        document.querySelectorAll('span[aria-label]').forEach((el) => {
          if (el instanceof HTMLElement && !el.getAttribute('role')) el.setAttribute('role', 'button');
        });
        // Ensure select pickers have aria-label
        document.querySelectorAll('select.ql-header').forEach((sel) => {
          if (sel instanceof HTMLSelectElement && !sel.getAttribute('aria-label')) {
            sel.setAttribute('aria-label', 'Header level');
          }
        });
      } catch (e) {
        // ignore
      }

      // Heuristic to avoid duplicate id collisions: append suffix to duplicate ids found in DOM
      const idMap = new Map<string, number>();
      const allEls = Array.from(document.querySelectorAll('[id]')) as HTMLElement[];
      allEls.forEach(el => {
        const id = el.id;
        if (!id) return;
        if (!idMap.has(id)) {
          idMap.set(id, 1);
        } else {
          const count = idMap.get(id) || 1;
          const newId = `${id}-${count}`;
          el.id = newId; // update DOM id
          idMap.set(id, count + 1);
        }
      });
      // No DOM mutation here — ClientHeader will mount inside the client provider.
    } catch (e) {
      // non-critical
      console.error('Auto-a11y script failed', e);
    }
  }, []);

  // Hide duplicate AppBars only in the right context: dashboard uses AuthenticatedShell chrome, not PublicHeader.
  // Logged-in users on `/` and other public routes still need PublicHeader (see showPublicShell) — do not hide it.
  useLayoutEffect(() => {
    try {
      const finalAuth = !!(localSession || session || isAuthenticated);
      const onDashboard = !!pathname?.startsWith('/dashboard');
      const appbars = Array.from(document.querySelectorAll('.MuiAppBar-root')) as HTMLElement[];
      if (finalAuth && onDashboard) {
        // Stray public AppBars should not stack over the dashboard shell.
        appbars.forEach((el) => {
          try {
            if (el.getAttribute && el.getAttribute('data-public-header') === 'true') {
              el.setAttribute('aria-hidden', 'true');
              el.style.display = 'none';
            }
          } catch (e) { /* ignore */ }
        });
      } else if (finalAuth && !onDashboard) {
        // e.g. landing while signed in — ensure public header is visible after visiting /dashboard
        appbars.forEach((el) => {
          try {
            if (el.getAttribute && el.getAttribute('data-public-header') === 'true') {
              el.removeAttribute('aria-hidden');
              el.style.removeProperty('display');
            }
          } catch (e) { /* ignore */ }
        });
      } else {
        // Unauthenticated: keep the first AppBar and hide subsequent duplicates.
        if (appbars.length > 1) {
          for (let i = 1; i < appbars.length; i++) {
            try {
              const el = appbars[i];
              if (el instanceof HTMLElement) {
                el.setAttribute('aria-hidden', 'true');
                el.style.display = 'none';
              }
            } catch (e) { /* ignore */ }
          }
        }
      }
    } catch (e) { /* ignore */ }
  }, [session, localSession, isAuthenticated, pathname]);

  return (
    <AppThemeProvider>
      <QueryProvider>
        {/* Prefer real server session when present so JWT/cookie auth wins over stale dev mock. */}
        <NextAuthClientProvider session={session || localSession}>
          {/* App shell: header, main, footer so footer can be anchored to bottom */}
          <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
          {/* Render public header/footer when unauthenticated and not on /dashboard. Auth pages (sign-in, sign-up) show public header/footer. */}
          {showPublicShell && <ClientHeader />}
            <main style={{ flex: 1 }}>{children}</main>
            {showPublicShell && <ClientFooter />}
          </div>
        </NextAuthClientProvider>
      </QueryProvider>
    </AppThemeProvider>
  );
}

// Note: Footer is rendered directly for public (unauthenticated) pages above.

// HeaderWidgets removed — ClientHeader (PublicHeader) is rendered directly above for unauthenticated pages.


